import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { useAccountStorage } from "../../../hooks/useAccountStorage";
import { Header, HeaderAction } from "../../Header";
import { sectionToIcon } from "../../type/default";
import { SetupStep } from "./setupRoutes";
import { SetupStepBar } from "./SetupStepBar";
import { BREEDER_BREEDINGS_PATH } from "../breeding/farmer/breedingRoutes";
import { isSetupResolved, markSetupResolved } from "./setupStorage";

/**
 * Coquille commune des quatre écrans : une page de l'espace éleveur comme les autres —
 * en-tête, navbar du layout — avec sa barre d'étapes et la sortie toujours disponible
 * (« Passer » au premier démarrage, « Retour » ensuite).
 *
 * C'est aussi le point de contrôle du type de compte. `RoleRoute` protège l'espace
 * éleveur, qui inclut les collaborateurs ; or le parcours crée des données, ce qu'un
 * compte de consultation ne fait pas.
 */
export const SetupLayout = ({
  step,
  breedingId,
  titleKey,
  introKey,
  children,
}: {
  /** Absent sur le mot d'accueil, qui précède la progression. */
  step?: SetupStep;
  breedingId?: number;
  titleKey: string;
  introKey?: string;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAccountStorage } = useAccountStorage();

  if (getAccountStorage()?.type !== "breeder") {
    return <Navigate to="/breeder" replace />;
  }

  // Premier démarrage : on peut passer le parcours. Ensuite, il sert à créer un élevage
  // de plus depuis la page Élevages, et on y retourne.
  const isFirstStart = !isSetupResolved();

  const skip = () => {
    markSetupResolved();
    navigate("/breeder", { replace: true });
  };

  return (
    <div className="p-4">
      <Header
        title={t(titleKey)}
        icon={sectionToIcon.section_breedings}
        actions={
          isFirstStart ? (
            <HeaderAction
              icon="arrow-right"
              variant="secondary"
              label={t("setup-skip")}
              onClick={skip}
            />
          ) : (
            <HeaderAction
              icon="arrow-left"
              variant="secondary"
              label={t("setup-back")}
              onClick={() => navigate(BREEDER_BREEDINGS_PATH)}
            />
          )
        }
      />

      {step && (
        <div className="mt-6">
          <SetupStepBar current={step} breedingId={breedingId} />
        </div>
      )}

      <div className="mt-6 mx-auto max-w-2xl">
        {introKey && (
          <p className="text-grey-readable mb-6 text-center">{t(introKey)}</p>
        )}

        {children}
      </div>
    </div>
  );
};

/** Bandeau d'erreur, identique sur les quatre écrans. */
export const SetupError = ({ message }: { message: string | null }) =>
  message ? (
    <div
      role="alert"
      className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg"
    >
      {message}
    </div>
  ) : null;
